// React Component
import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../stores'
import FamlaServices from '../services/api/FamlaServices'
import { slice } from '../utils'
import Spiner from '../components/Spiner'
import { Dropdown } from '../components'
import { Routes } from '../routes'
import { openPDFInViewer } from '../utils'
import CreateCampaignModal from '../views/campaigns/components/CreateCampaignModal'
import CampaignOperation from '../views/campaigns/components/CampaignOperation'
import { useLocation } from 'react-router-dom'
import { navigateToInterviewWithFallback } from '../utils/smartNavigation'

interface Campaign {
    _id: string
    name: string
    description: string
    objective: string
    status: string
    created_at: string
    updated_at: string
    creator_id: string
    company_id: string
    industrie_id?: string
    process_ids?: string[]
}

interface ProcessDetails {
    _id: string
    title: string
    description?: string
    interview_id?: string
}

interface CampaignNavLayoutProps {
    updateDisplayedCampaign: (campaignId: string) => void
}

const CampaignNavLayout: React.FC<CampaignNavLayoutProps> = ({
    updateDisplayedCampaign
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const users = useAppSelector((state) => state.auth)
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const campaignId = searchParams.get('id')
    const campaignName = searchParams.get('name')

    const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);

    // State
    const [campaigns, setCampaigns] = useState<Campaign[]>([])
    const [processes, setProcesses] = useState<ProcessDetails[]>([])
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true)
    const [isLoadingProcesses, setIsLoadingProcesses] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
    const [selectedCampaignName, setSelectedCampaignName] = useState<string | null>(null)
    const handleCampaignCreated = (campaign: Campaign) => {
        setCampaigns([...campaigns, campaign])
        handleCampaignClick(campaign)
    }

    // Fetch campaigns on mount and when refreshKey changes
    useEffect(() => {
        fetchCampaigns()
        if (campaignId) {
            setSelectedCampaignId(campaignId)
            setSelectedCampaignName(campaignName)
        }
    }, [])

    useEffect(() => {
        if (selectedCampaignId) {
            fetchProcesses()
        }
    }, [selectedCampaignId])

    // Fetch campaigns from API
    const fetchCampaigns = async () => {
        try {
            setIsLoadingCampaigns(true)
            const data = await FamlaServices(
                "api_host",
                "/campaign/",
                "GET",
                "",
                users.access_token
            )

            console.log("campaigns list", data.body.data);
            //TODO setSelectedCampaignId

            if (data.status === 200 && data.body.data) {
                setCampaigns(data.body.data)
            }
        } catch (error) {
            console.error('Error fetching campaigns:', error)
        } finally {
            setIsLoadingCampaigns(false)
        }
    }

    // Fetch processes (using 3 random processes as example)
    const fetchProcesses = async () => {
        try {
            setIsLoadingProcesses(true)
            const data = await FamlaServices(
                "api_host",
                `/campaign/${selectedCampaignId}/processes`,
                "GET",
                "",
                users.access_token
            )

            if (data.status === 200 && data.body.data) {
                // Get first 3 processes as example
                setProcesses(data.body.data)
            }
        } catch (error) {
            console.error('Error fetching processes:', error)
        } finally {
            setIsLoadingProcesses(false)
        }
    }

    // Filter campaigns based on search
    const filteredCampaigns = useMemo(() => {
        if (!searchValue.trim()) {
            return campaigns
        }
        const searchLower = searchValue.toLowerCase()
        return campaigns.filter((campaign) =>
            campaign.name?.toLowerCase().includes(searchLower)
        )
    }, [campaigns, searchValue])

    // Handle campaign click
    const handleCampaignClick = (campaign: Campaign) => {
        // TODO: add a focus on the selected campaign
        console.log("campaignIdclick", campaignId);
        setSelectedCampaignId(campaign._id)
        setSelectedCampaignName(campaign.name)
        updateDisplayedCampaign(campaign._id)
        
    }

    // Handle new campaign button
    const handleNewCampaign = () => {
        setShowCreateCampaignModal(true);
    }

    // Refresh campaigns after creation
    const refreshCampaigns = () => {
        fetchCampaigns()
    }

    // Handle campaign updated successfully
    const handleCampaignUpdated = (updatedCampaign: Campaign) => {
        setCampaigns(campaigns.map(c => c._id === updatedCampaign._id ? updatedCampaign : c));
        if (selectedCampaignId === updatedCampaign._id) {
            setSelectedCampaignName(updatedCampaign.name);
        }
    }

    // Handle campaign delete
    const handleCampaignDeleted = (deletedCampaignId: string) => {
        setCampaigns(campaigns.filter(c => c._id !== deletedCampaignId));
        // Clear selection if deleted campaign was selected
        if (selectedCampaignId === deletedCampaignId) {
            setSelectedCampaignId(null);
            setSelectedCampaignName(null);
        }
    }

    // Expose refresh function (can be called from parent)
    useEffect(() => {
        // This will be called when campaigns need to be refreshed
        // Parent component can trigger this by changing a prop if needed
    }, [])

    return (
        <main className="container-fluid m-2 ps-2 position-relative" style={{ height: "100%", width: "100%" }}>
            <div className="w-100 overflow-auto h-100" id="navcontainer">
                <div style={{ marginBottom: "100px" }}>
                    {/* Search Bar */}
                    <div className="w-100 mb-2">
                        <div className="position-relative bg-dark rounded-4 d-flex align-items-center px-2 py-1">
                            <i className="fa-solid fa-search text-light pe-2" style={{ fontSize: '0.9rem' }}></i>
                            <input
                                type="text"
                                placeholder={t('campaigns.search')}
                                className="form-control bg-transparent text-light border-0 p-0"
                                style={{ fontSize: '0.95rem', outline: 'none', boxShadow: 'none' }}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* New Campaign Button */}
                    <button
                        onClick={handleNewCampaign}
                        type="button"
                        className="text-decoration-none text-light fs-6 bg-dark border-0 rounded-4 mb-2 px-1"
                    >
                        <div className="py-1 px-1 border-0 text-gradient">
                            <span>
                                <i className="fa-solid fa-plus pe-0"></i>
                            </span>
                            <span className="ms-1">{t('campaigns.newCampaign')}</span>
                        </div>
                    </button>

                    {/* Campaigns List */}
                    <div className="w-100 mb-3">
                        <h4 className="text-gradient fs-6 mb-2">
                            {t('campaigns.recentCampaigns')}
                        </h4>
                        <div className="hover-scroll d-flex flex-column" style={{height:"100%", maxHeight:"30vh"}}>
                            {isLoadingCampaigns ? (
                                <Spiner className="spinner-border-sm ps-1" />
                            ) : (
                                <>
                                    {filteredCampaigns.length === 0 && searchValue ? (
                                        <div className="text-secondary small text-center py-3">
                                            {t('words.noResults')}
                                        </div>
                                    ) : campaigns.length === 0 ? (
                                        <div className="text-white ms-5">
                                            <i className="fa-solid fa-triangle-exclamation"></i>
                                            <span className="ms-2 small text-secondary">
                                                {t('campaigns.noCampaigns')}
                                            </span>
                                        </div>
                                    ) : (
                                        filteredCampaigns.map((campaign) => (
                                            <div
                                                key={campaign._id}
                                                className={`bg-red-200 h-1000 text-light fs-6 text-decoration-none border-0 rounded-4 btn btn-outline-secondary btn-md mb-2 d-flex justify-content-between align-items-center ${
                                                    selectedCampaignId === campaign._id ? 'active' : ''
                                                }`}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedCampaignId === campaign._id ? 'rgba(108, 117, 125, 0.3)' : 'transparent'
                                                }}
                                            >
                                                <div
                                                    onClick={() => handleCampaignClick(campaign)}
                                                    className="d-flex text-nowrap flex-grow-1"
                                                >
                                                    <span>
                                                        <i className="fa-regular fa-message small me-2"></i>
                                                    </span>
                                                    <span>{slice(campaign.name, 23)}</span>
                                                </div>
                                                <CampaignOperation
                                                    campaignId={campaign._id}
                                                    campaignName={campaign.name}
                                                    onUpdated={handleCampaignUpdated}
                                                    onDeleted={handleCampaignDeleted}
                                                />
                                            </div>
                                        ))
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Processes List */}
                    <div className="w-100 mt-3">
                        <h4 className="text-gradient fs-6 mb-2">
                            {t('campaigns.processes')}
                        </h4>
                        <div className="hover-scroll d-flex flex-column" style={{height:"100%", maxHeight:"30vh"}}>
                            {isLoadingProcesses ? (
                                <Spiner className="spinner-border-sm ps-1" />
                            ) : (
                                <>
                                    {processes.length === 0 ? (
                                        <div className="text-white ms-5">
                                            <i className="fa-solid fa-triangle-exclamation"></i>
                                        </div>
                                    ) : (
                                        processes.map((process) => (
                                            <div
                                                key={process._id}
                                                className="text-light fs-6 text-decoration-none border-0 rounded-4 btn btn-outline-secondary btn-md mb-2"
                                                style={{ cursor: 'pointer' }}
                                                onClick={async () => {
                                                    // [NAVIGATION INTELLIGENTE] Utiliser la navigation avec switch automatique
                                                    const success = await navigateToInterviewWithFallback(
                                                        process._id, // Utiliser process._id au lieu de interview_id
                                                        users.access_token,
                                                        (path: string) => navigate(`${path}?campaignId=${selectedCampaignId}&campaignName=${selectedCampaignName}`),
                                                        {
                                                            onSwitchStart: () => console.log('🔄 [CampaignNavLayout] Company switch started for process:', process._id),
                                                            onSwitchComplete: () => console.log('✅ [CampaignNavLayout] Company switch completed'),
                                                            onError: (error: string) => {
                                                                console.error('❌ [CampaignNavLayout] Smart navigation error:', error);
                                                                // Fallback vers navigation directe en cas d'erreur
                                                                window.location.href = Routes.PROCESS_INTERVIEW + process.interview_id + "?campaignId=" + selectedCampaignId + "&campaignName=" + selectedCampaignName;
                                                            }
                                                        }
                                                    );
                                                    
                                                    if (!success) {
                                                        console.warn('⚠️ [CampaignNavLayout] Smart navigation failed, using direct navigation');
                                                        window.location.href = Routes.PROCESS_INTERVIEW + process.interview_id + "?campaignId=" + selectedCampaignId + "&campaignName=" + selectedCampaignName;
                                                    }
                                                }}
                                            >
                                                <div className="d-flex text-nowrap">
                                                    <span>
                                                        <i className="fa-regular fa-message small me-2"></i>
                                                    </span>
                                                    <span>{slice(process.title, 23)}</span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer with Help and User Dropdown */}
            <div className="position-absolute w-100" style={{ bottom: "0px", backgroundColor: "#1F1F21", marginLeft: "-16px" }}>
                <div className="mb-3 bg-transparent ps-3">
                    <Dropdown
                        dropName={t('words.help')}
                        dropdownType="dropdown"
                        iconClassName='fa-regular fa-circle-question me-2'
                        buttonDropClassName='btn btn-color text-white border-0'
                        styleButton={{}}
                        deleteProcess={() => { }}
                        action4={t('navigation.contactUs')}
                        action5={t('navigation.privacyHub')}
                        theRouteOfAction4={Routes.CONTACTUS}
                        theRouteOfAction5={Routes.PRIVACY}
                        faq={t('words.faq')}
                        routeFaq={Routes.FAQ}
                        editdiadram={t('navigation.userGuide')}
                        runEditor={openPDFInViewer}
                        buttonLabel={t('navigation.transcript')}
                        onClickAction={() => {}}
                    />

                    <Dropdown
                        dropName={users.user.fullname}
                        dropdownType="dropdown"
                        iconClassName='fa-regular fa-user me-2'
                        buttonDropClassName='btn btn-secondary bg-teal rounded-4 mt-2 border-0'
                        styleButton={{}}
                        deleteProcess={() => { }}
                        action2={t('navigation.logOut')}
                        theRouteOfAction4=""
                        theRouteOfAction5=""
                        dataBsTarget="#inviteUser"
                        routeFaq=""
                        action6={t('words.languages')}
                        dataBsTargetAction6="#changeLanguageModal"
                    />
                </div>
            </div>
            {/* Create Campaign Modal */}
            <CreateCampaignModal
                isOpen={showCreateCampaignModal}
                onClose={() => setShowCreateCampaignModal(false)}
                onSuccess={handleCampaignCreated}
            />
        </main>
    )
}

export default CampaignNavLayout

